import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../../Hooks/useAuth';
import { getStaffingMisBaseURL } from '../../../../Api/request/staffingMis';
import {
  WEEKLY_UTILIZATION_TITLE, weeklyUtilizationFilters, weeklyUtilizationColumns, handleWeeklyUtilizationResponse,
  defaultWeeklyUtilAppliedFilters,
} from './weeklyUtilizationReportConfig';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import '../StaffingMis.scss';

const baseToolBarSettings = {
  showToolBarHeading: true,
  showAddNew: false,
  showExpandCollapse: false,
  showColumnCustomize: false,
  showHelp: false,
  showSearch: false,
  showSaveView: false,
  showReset: false,
};

const WeeklyUtilizationReport = ({ embedded = false, externalAppliedFilters = null }) => {
  const { userProfile } = useAuth();
  const hasAccess = (userProfile?.permissions?.['mis:staffing-mis'] || [])
    .some((action) => ['read', 'write', 'update', 'delete', 'create', 'edit'].includes(action.toLowerCase()));
  const [localAppliedFilters, setLocalAppliedFilters] = useState(defaultWeeklyUtilAppliedFilters);
  const appliedFilters = externalAppliedFilters || localAppliedFilters;
  const reportRequestKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    setLocalAppliedFilters(action === 'reset' ? [] : filters || []);
  }, []);

  const commonSettings = useMemo(() => ({
    isModifiedFilter: true,
    tableUtilityConfig: { isModifiedFilter: true },
    baseURL: getStaffingMisBaseURL(),
    showToolBar: true,
    showPagination: false,
    headers: { token: userProfile?.access_token || '' },
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || {},
      roleDetails: userProfile?.roleDetails || [],
      employeeId: userProfile?.employeeId || '',
      globalColors: userProfile?.siteConfiguration?.global_colors || {},
    },
  }), [userProfile]);

  const headerConfiguration = useMemo(() => ({
    generalSettings: {
      ...commonSettings,
      enableApiFiltering: false,
      apiEndpoints: {
        getFilteredData: { endpoint: '', enabled: false },
        download: { endpoint: 'export', enabled: true, additionalPayloadData: { filter: appliedFilters } },
        getAllViews: { endpoint: '', enabled: false },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
      },
      handler: { onFilterUpdate: handleFilterUpdate, setTableStateHandler: () => { } },
      toolBarSettings: {
        ...baseToolBarSettings,
        toolBarHeading: 'Staffing MIS',
        showFilterOption: true,
        showColumnFilterMenu: true,
        showFilters: true,
        showDownloadExcel: true,
        downloadExcelSheetName: WEEKLY_UTILIZATION_TITLE,
      },
    },
    showFilterOption: true,
    showExpandCollapse: false,
    tableSettings: {
      size: 'small',
      tableConfig: weeklyUtilizationFilters,
      enableTreeTable: false,
      showExpandCollapse: false,
      paginationConfig: { page: 1, limit: 10, total: 0, rowsPerPageOptions: [10] },
    },
  }), [commonSettings, handleFilterUpdate, appliedFilters]);

  const reportConfiguration = useMemo(() => ({
    generalSettings: {
      ...commonSettings,
      enableApiFiltering: true,
      additionalPayloadData: { filter: appliedFilters, section: 'weeklyUtilization' },
      apiEndpoints: {
        getFilteredData: { endpoint: '', enabled: true },
        download: {
          endpoint: 'export',
          enabled: true,
          additionalPayloadData: { filter: appliedFilters, section: 'weeklyUtilization' },
        },
        getAllViews: { endpoint: '', enabled: false },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
      },
      handler: { handleFilteredApiResponse: handleWeeklyUtilizationResponse },
      toolBarSettings: {
        ...baseToolBarSettings,
        toolBarHeading: WEEKLY_UTILIZATION_TITLE,
        showFilterOption: false,
        showColumnFilterMenu: false,
        showFilters: false,
        showDownloadExcel: true,
        downloadExcelSheetName: WEEKLY_UTILIZATION_TITLE,
      },
    },
    showExpandCollapse: false,
    tableSettings: {
      size: 'small',
      tableScrollHeight: '65vh',
      tableConfig: weeklyUtilizationColumns,
      enableTreeTable: false,
      showExpandCollapse: false,
      pageSize: 10000,
      showPageSizeSelector: false,
      paginationConfig: { page: 1, limit: 10000, total: 0, rowsPerPageOptions: [10000] },
    },
  }), [commonSettings, appliedFilters]);

  if (!hasAccess) {
    return <div className="staffing-mis-outer-layer">You do not have permission to view Staffing MIS.</div>;
  }
  const reportTable = (
    <div className="staffing-mis-section weekly-utilization-section">
      <div className="staffing-mis-table-utility table-utility weekly-bench-report-table">
        <TableUtility
          key={reportRequestKey}
          configuration={reportConfiguration}
          externalFilters={appliedFilters}
          showExpandCollapse={false}
        />
      </div>
    </div>
  );

  if (embedded) return reportTable;

  return (
    <div className="staffing-mis-outer-layer">
      <div className="staffing-mis-table-container">
        <div className="staffing-mis-table-utility table-utility staffing-toolbar-only">
          <TableUtility configuration={headerConfiguration} data={[]} showFilterOption showExpandCollapse={false} />
        </div>
        {reportTable}
      </div>
    </div>
  );
};

WeeklyUtilizationReport.propTypes = {
  embedded: PropTypes.bool,
  externalAppliedFilters: PropTypes.arrayOf(PropTypes.object),
};

export default WeeklyUtilizationReport;
